<template>
    <div style="height: 100%">
        <div id="main2" v-show="!rotaAninhada">
            <container-loader v-if="isLoading && !$store.getters.isLoading" />
            <div class="page">
                <div class="vertical-flow">
                    <div class="listing-header">
                        <div class="title-breadcrumb">
                            <h1 class="title">
                                {{ $t('atendimento') }}
                            </h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{ $t('promotor') }}</a>
                                    </li>
                                    <li>
                                        <a class="a-inativo">{{ $t('relatorios') }}</a>
                                    </li>
                                    <li>
                                        <a aria-current="page">{{ $t('atendimento') }}</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div class="header-buttons">
                            <button
                                class="delete-btn mr-4"
                                type="button"
                                @click="$router.push({ name: 'promotor-contrato-list' })"
                            >
                                {{ $t('voltar') }}
                            </button>
                            <div
                                @click="hasAtendimentosSelected ? showModalExportacaoPDF = true : null"
                                class="pr-1"
                            >
                                <b-icon
                                    icon="file-pdf"
                                    :type="hasAtendimentosSelected ? 'is-primary' : 'is-dark'"
                                    size="is-medium"
                                    class="is-medium is-clickable"
                                    v-tippy 
                                    :title="hasAtendimentosSelected 
                                        ? $t('msg-tooltip-pdf') 
                                        : $t('necessario-selecionar-atendimentos')
                                    "
                                />
                            </div>
                        </div>
                    </div>
                    <div class="filters-bar mb-3">
                        <div class="filters-bar-form form-columns-fit">
                            <div class="columns">
                                <div class="column is-6">
                                    <search-bar ref="search" :onSearch="debouncedOnSearch" tooltipLabel="busca-atendimento-relatorio" />
                                </div>
                                <div class="column is-3">
                                    <verified-date-picker
                                        dateType="startDate"
                                        :value="datainicio"
                                        :endDate="datafim"
                                        title="data-inicio"
                                        @input="changeStartDate"
                                        :showClean="false"
                                        required
                                        :invalid="datainicio == null"
                                    />
                                </div>
                                <div class="column is-3">
                                    <verified-date-picker
                                        dateType="endDate"
                                        :value="datafim"
                                        :startDate="datainicio"
                                        title="data-fim"
                                        @input="changeEndDate"
                                        :showClean="false"
                                        required
                                        :invalid="datafim == null"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="filters-bar-actions">
                            <b-button
                                type="is-primary"
                                icon-right="filter"
                                @click="showFilter = !showFilter"
                                v-tippy 
                                :title="$t('filtros')"
                            />
                        </div>
                    </div>

                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            :data="atendimentoList"
                            class="data-table"
                            @sort="sortTable"
                            :checked-rows.sync="atendimentosSelected"
                            :is-row-checkable="isLowerThanMaxAndIsChecked"
                            :custom-is-checked="
                                (selecionados, linha) =>
                                    selecionados.id === linha.id
                            "
                            checkable
                            backend-sorting
                        >
                            <b-table-column
                                v-for="(f, i) in fields"
                                :key="i"
                                :field="f.key"
                                :width="f.width"
                                :label="$t(f.key)"
                                :sortable="f.sortable"
                                v-slot="props"
                            >
                                <div v-if="f.key == 'codigo'">
                                    <router-link
                                        :to="{
                                            name: 'promotor-atendimento-view',
                                            params: {
                                                idatendimento: props.row.id,
                                            },
                                        }"
                                    >
                                        {{ props.row[f.key] }}
                                    </router-link>
                                </div>
                                <div v-else-if="f.key == 'contrato'">
                                    <router-link
                                        :to="{
                                            name: 'promotor-atendimento-view',
                                            params: {
                                                idatendimento: props.row.id,
                                            },
                                        }"
                                    >
                                        {{ props.row[f.key] }}
                                    </router-link>
                                </div>
                                <div v-else-if="f.key == 'dataatendimento'">
                                    {{ props.row[f.key] | moment('L') }}
                                </div>
                                <template v-else>
                                    {{ props.row[f.key] }}
                                </template>
                            </b-table-column>
                        </b-table>
                        <PaginationViewer
                            class="mb-0"
                            :resetPagination="pagination.resetPagination"
                            :totalItems="pagination.totalRows"
                            :perPageOptions="pagination.perPageOptions"
                            :optionStarted="pagination.optionStarted"
                            @change="updatePagination"
                        />
                    </div>
                </div>
            </div>
        </div>

        <lateral-content :isLoading="isLoading" v-model="showFilter" footer>
            <template v-slot:footer-right>
                <b-button
                    type="is-warning is-silver"
                    icon-right="backspace"
                    @click="onClearFilters()"
                    class="mr-2"
                    :disabled="isLoading"
                >
                    {{ $t('limpar') }}
                </b-button>
                <b-button
                    type="is-primary"
                    icon-right="filter"
                    @click="
                        carregarDados();
                        showFilter = false;
                    "
                    :disabled="isLoading"
                >
                    {{ $t('filtrar') }}
                </b-button>
            </template>
            <FiltroAtendimento
                ref="filtroRef"
                :filtersReset="filtersReset"
                @update-filtro="updateFiltro"
                @update-bool-filters="updateBoolFilters"
            />
        </lateral-content>

        <lateral-content 
            :isLoading="isLoading" 
            v-model="showModalExportacaoPDF" 
            compact 
            header
            headerHideClose
            :title="$t('modelo-de-exportacacao-pdf')"
            footer
        >
            <ModalExportacaoPDF
                ref="modalExportacaoPDF"
                @updateModeloSelected="updateModeloSelected"
            />
            <template v-slot:footer-right>
                <b-button
                    type="is-primary"
                    icon-right="file-pdf"
                    @click="
                        exportaPDF();
                    "
                >
                    {{ $t('gerar-pdf') }}
                </b-button>
            </template>
        </lateral-content>

        <router-view />
    </div>
</template>
<script>
import ProAtendimentoService from '@/services/promotor/ProAtendimentoService.js';
import FiltroRelatorioService from '@/services/FiltroRelatorioService.js';
import ClienteService from '@/services/promotor/ClienteService.js';
import ContainerLoader from '@/components/content/loader/ContainerLoader.vue';
import FiltroAtendimento from '@/components/promotor/relatorio/atendimento/FiltroAtendimento.vue';
import ModalExportacaoPDF from '@/components/promotor/relatorio/atendimento/ModalExportacaoPDF.vue';

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name.includes('promotor-atendimento') && to.name != 'promotor-atendimento-list') {
            this.rotaAninhada = true;
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
        }
        next();
    },
    components: {
        FiltroAtendimento,
        ModalExportacaoPDF,
        ContainerLoader,
    },
    computed: {
        hasAtendimentosSelected: function () {
            return this.atendimentosSelected.length > 0;
        }
    },
    data() {
        return {
            rotaAninhada: false,

            service: new ProAtendimentoService(this.$http, this.$router),
            filtroService: new FiltroRelatorioService(this.$http, this.$router),
            clienteService: new ClienteService(this.$http, this.$router),

            atendimentoList: [],
            atendimentosSelected: [],
            // Todos as colunas da tabela
            fields: [
                { key: 'codigo', width: null, sortable: true },
                { key: 'contrato', width: null, sortable: true },
                { key: 'cliente', width: null, sortable: true },
                { key: 'rsocial', width: null, sortable: true },
                { key: 'lps', width: null, sortable: true },
                { key: 'colaborador', width: null, sortable: true },
                { key: 'dataatendimento', width: null, sortable: true },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            filters: {
                idempresaList: [],
                idcidadeList: [],
                idestadoList: [],
                idregiaoList: [],
                idclienteList: [],
                idusuarioList: [],
                idredeList: [],
                idbandeiraList: [],
                idlpsList: [],
                idcontratoList: [],
                idtiposervicoList: [],
                idjustificativaList: [],
                column: 'datahorainc',
                order: 'desc',
                searchQuery: '.*',
                situacao: 2,
                idcontrato: 0,
                idlps: 0,
                datainicio: null,
                datafim: null,
                withRupturaTroca: false,
                withFormulario: false,
                withFoto: false,
                withPesquisa: false,
                withVencimento: false,
                withShare: false,
                withAssinatura: false
            },
            msgs: [],
            showFilter: false,
            showModalExportacaoPDF: false,
            filtersReset: false,
            datainicio: new Date(this.$moment().subtract(30, 'days')),
            datafim: new Date(),

            isLoading: false,
            isEmpty: false,

            modalOpen: false,
            debouncedOnSearch: null,
            permissao: {},

            limparFiltro: false,
            maxCheckedAllowed: 20,
            modeloSelected: null,
        };
    },
    watch: {
        atendimentosSelected: function (newAtendimentosSelected) {
            if(newAtendimentosSelected.length > this.maxCheckedAllowed) {
                this.atendimentosSelected = newAtendimentosSelected.slice(0, this.maxCheckedAllowed);
            }
        }
    },
    created() {
        this.rotaAninhada =
            this.$route.name.includes('promotor-atendimento') &&
            this.$route.name != 'promotor-atendimento-list';

        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess(
            'promotor',
            'relatorios',
            'pro-report-atendimento'
        );

        !this.permissao.read &&
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'atendimentos' },
            });

        this.debouncedOnSearch = this.debounce(this.onSearch, 500);

        this.carregarDados();
    },
    methods: {
        // Recebe o $emit do VerifiedDatePicker, adiciona o valor na variavel local que retorna para a prop do VerifiedDatePiker
        changeStartDate(date) {
            if (date.getTime() != this.datainicio.getTime()) {
                this.datainicio = date;
                if (!this.isNull(this.datainicio)) this.carregarDados();
            }
        },
        changeEndDate(date) {
            if (date.getTime() != this.datafim.getTime()) {
                this.datafim = date;
                if (!this.isNull(this.datafim)) this.carregarDados();
            }
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        onSearch(value) {
            this.filters.searchQuery = this.removeToSearch(value);
            this.pagination.currentPage = 1;
            this.carregarDados();
        },
        sortTable(field, order) {
            this.filters.column = field;
            this.filters.order = order;
            this.carregarDados();
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;
            this.fillFilter(currentPage);
        },
        fillFilter(currentPage = 1) {
            this.filters.perPage = this.pagination.perPage;
            this.filters.currentPage = currentPage;
            this.filters.searchQuery = this.filters.searchQuery || '.*';

            this.filters.datainicio = this.datainicio
                ? this.$moment(this.datainicio).format('YYYY-MM-DD')
                : null;
            this.filters.datafim = this.datafim
                ? this.$moment(this.datafim).format('YYYY-MM-DD')
                : null;

            this.carregarDadosAtendimentoContrato();
        },
        carregarDadosAtendimentoContrato() {
            this.service
                .findAll(this.filters)
                .then(this.mapItems)
                .then((value) => {
                    this.atendimentoList = value;
                    this.isEmpty = this.atendimentoList.length <= 0;
                })
                .catch((e) => {
                    this.atendimentoList = [];
                    this.isEmpty = true;
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            return body.content.map((m) => {
                return {
                    id: m.idatendimento,
                    codigo: m.idatendimentoerp,
                    idcliente: m.proCliente.idcliente,
                    idEmpresa: m.empresa.idempresa,
                    cliente: m.proCliente.nome,
                    rsocial: m.proCliente.rsocial,
                    contrato: m.contrato.descricao,
                    lps: m.proLps.nome,
                    colaborador: m.usuario.nome,
                    dataatendimento: m.dataatendimento,
                };
            });
        },
        onClearFilters() {
            this.$root.confirm({
                message: this.$t('msg-confirm-limpar-filtros'),
                onConfirm: () => this.clearFilter(),
            });
        },
        clearFilter() {
            this.filtersReset = true;

            this.filters.datainicio = new Date(this.$moment().subtract(30, 'days'));
            this.filters.datafim = new Date();
            this.filters.searchQuery = '.*';

            this.filters.idempresaList = [];
            this.filters.idcidadeList = [];
            this.filters.idestadoList = [];
            this.filters.idregiaoList = [];
            this.filters.idclienteList = [];
            this.filters.idusuarioList = [];
            this.filters.idredeList = [];
            this.filters.idbandeiraList = [];
            this.filters.idlpsList = [];
            this.filters.idcontratoList = [];
            this.filters.idtiposervicoList = [];
            this.filters.idjustificativaList = [];

            this.$refs.search.cleanSearch();

            setTimeout(() => {
                this.filtersReset = false;
                this.showFilter = false;
                this.carregarDados();
            }, 1000);
        },
        updateFiltro(filtro) {
            this.filters[filtro.nomeFiltro] = filtro.valor;
        },
        updateBoolFilters(boolFilters) {
            this.filters = {
                ...this.filters,
                ...boolFilters
            }
        },
        isLowerThanMaxAndIsChecked(row) {
            const isInLimit = this.atendimentosSelected.length < this.maxCheckedAllowed;
            const rowIsSelected = this.atendimentosSelected.some(atendimento => atendimento.id === row.id);

            return isInLimit || rowIsSelected;
        },
        async exportaPDF() {
            if (this.invalidExportaPDFRequest()) return;

            this.isLoading = true;

            const idConfJasper = this.modeloSelected.id;

            const atendimentosPorEmpresa = this.separaAtendimentosPorEmpresas(this.atendimentosSelected);

            const atendimentosPorEmpresaCliente = this.separaAtendimentosEmpresasPorClientes(atendimentosPorEmpresa);

            const requests = atendimentosPorEmpresaCliente.map(({idEmpresa, clientes}) => {
                const requestBody = {
                    idEmpresa: idEmpresa,
                    clientes: clientes
                }

                return this.$axios.post('/report-promotor/services/report-data', requestBody)
            })

            this.showModalExportacaoPDF = false;

            const responsesReportData = await Promise.all(requests)

            const responsesJasperPdf = responsesReportData.map(({data}) => {
                return this.$axios.post(`/report-promotor/services/exportacao/pdf/${idConfJasper}`, data, {
                    responseType: 'blob',
                    timeout: 90000
                })
            })

            Promise.all(responsesJasperPdf)
            .then((response) => {
                response.forEach(({data}) => {
                    const blob = new Blob([data], { type: 'application/pdf' });

                    const fileURL = window.URL.createObjectURL(blob);

                    window.open(fileURL, '_blank');

                    setTimeout(() => window.URL.revokeObjectURL(blob), 5000)
                })
            })
            .catch((e) => this.$root.showMessage('error', e.message))
            .finally((_) => this.isLoading = false)
        },
        invalidExportaPDFRequest() {
            if(this.modeloSelected === null) {
                const modalExportacaoElement = this.$refs.modalExportacaoPDF;

                modalExportacaoElement.$refs.modeloSelect.focusedOut = true;

                return true;
            }

            return false;
        },
        /**
         * Separa o array fornecido de 'atendimentos' em grupos com base na propriedade 'idEmpresa'.
         * Cada grupo contém 'idEmpresa' e um array de 'idsAtendimentos' associados a essa 'idEmpresa'.
         *
         * @param {Array} atendimentos - O array de 'atendimentos' a ser separado.
         * @returns {Array} - Um array de objetos representando os 'atendimentos' separados por 'idEmpresa'.
         */
        separaAtendimentosPorEmpresas(atendimentos) {
            const empresasAtendimentos = atendimentos.reduce((result, atendimento) => {
                const { idEmpresa, idcliente, id } = atendimento;
                const empresa = result.find(emp => emp.idEmpresa === idEmpresa);
                if (empresa) {
                    empresa.idsAtendimentos.push({ idCliente: idcliente, idAtendimento: id });
                } else {
                    result.push({ idEmpresa, idsAtendimentos: [{ idCliente: idcliente, idAtendimento: id }] });
                }
                return result;
            }, []);
            return empresasAtendimentos;
        },
        /**
         * Separa o array fornecido de 'empresasAtendimentos' em grupos com base na propriedade 'idCliente'.
         * Cada grupo contém 'idCliente' e um array de 'idsAtendimentos' associados a esse 'idCliente'.
         *
         * @param {Array} empresasAtendimentos - O array de 'empresasAtendimentos' a ser separado.
         * @returns {Array} - Um array de objetos representando os 'empresasAtendimentos' separados por 'idCliente'.
         */
        separaAtendimentosEmpresasPorClientes(empresasAtendimentos) {
            return empresasAtendimentos.map(empresaAtendimentos => {
                const clientes = empresaAtendimentos.idsAtendimentos.reduce((result, atendimento) => {
                    const { idCliente, idAtendimento } = atendimento;
                    const cliente = result.find(cli => cli.idCliente === idCliente);
                    if (cliente) {
                        cliente.idsAtendimentos.push(idAtendimento);
                    } else {
                        result.push({ idCliente, idsAtendimentos: [idAtendimento] });
                    }
                    return result;
                }, []);
                return { idEmpresa: empresaAtendimentos.idEmpresa, clientes };
            });
        },
        updateModeloSelected(newModeloSelected) {
            this.modeloSelected = newModeloSelected;
        }
    },
};
</script>
<style lang="scss" scoped>
#main2 {
    padding: 0 20px;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;

        // Barra de filtro
        .filters-bar {
            display: flex;
            align-items: stretch;
            .filters-bar-form {
                flex-grow: 1;
            }
            .filters-bar-actions {
                display: flex;
                padding-bottom: 2px;
                align-items: stretch;
            }

            @media screen and (min-width: 851px) {
                .filters-bar-actions {
                    align-self: flex-end;
                    margin-left: 10px;
                    margin-bottom: 12px;
                }
            }
            @media screen and (max-width: 850px) {
                flex-direction: column;
                .filters-bar-actions {
                    align-self: center;
                    margin-top: 10px;
                }
            }
        }
    }
    // Filtro lateral
    $width: 400px;
    .filtros-right {
        z-index: 100;
        padding: 15px;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
        background-color: $darker-bg-color;
        width: $width;
        height: 80%;
        position: fixed;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        border-top: solid 1px #d9d9d9;
        border-left: solid 1px #d9d9d9;
        border-bottom: solid 1px #d9d9d9;
        transition: 0.6s;
        -webkit-box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);
        box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);

        .filtros-container {
            margin-bottom: 60px;
        }

        .filtros-footer {
            border-bottom-left-radius: 5px;
            background-color: #fff;
            position: absolute;
            padding: 10px;
            bottom: 0;
            left: 0;
            right: 0;
            -webkit-box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);
            box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);

            .is-silver {
                color: $semi-title-color;
                box-shadow: inset 0px 0px 2px $dark-gray-color-opacity;
                @include exclude-button();
            }
        }
    }
    .hide-filtros-right {
        margin-right: -$width;
    }
    @media screen and (min-height: 850px) {
        .filtros-right {
            height: 50%;
        }
    }
    @media screen and (max-width: 640px) {
        $width: 95%;
        .filtros-right {
            width: $width;
        }
        .hide-filtros-right {
            margin-right: -$width;
        }
    }
}
</style>
